import React, { useState, useEffect } from 'react';
import './Settings.css';

const Settings = () => {
  const [collapsedSections, setCollapsedSections] = useState({
    appearance: false,
    chat: false,
    privacy: false,
    notifications: false,
  });

  const defaultSettings = {
    // Appearance
    theme: 'dark',
    fontSize: 'medium',

    // Chat Preferences
    autoSaveChats: true,
    showTypingIndicator: true,
    messageFormat: 'formatted',

    // Privacy
    dataCollection: false,
    shareAnalytics: false,

    // Notifications
    emailNotifications: true,
    chatNotifications: true,
    documentNotifications: true,
  };

  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('appSettings');
    return saved ? JSON.parse(saved) : defaultSettings;
  });

  useEffect(() => {
    // Apply theme when settings change
    if (settings.theme) {
      document.documentElement.setAttribute('data-theme', settings.theme);
    }
  }, [settings.theme]);

  const handleToggle = (setting) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handleSelect = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleSaveSettings = () => {
    try {
      localStorage.setItem('appSettings', JSON.stringify(settings));
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings. Please try again.');
    }
  };

  const handleResetSettings = () => {
    if (window.confirm('Are you sure you want to reset all settings to default?')) {
      setSettings(defaultSettings);
      localStorage.setItem('appSettings', JSON.stringify(defaultSettings));
      alert('Settings reset to default!');
    }
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Customize your experience and preferences</p>
      </div>

      {/* Appearance Settings */}
      <div className="settings-section">
        <div className="section-header" onClick={() => toggleSection('appearance')} style={{ cursor: 'pointer' }}>
          <div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="1" x2="12" y2="3" stroke="currentColor" strokeWidth="2"/>
              <line x1="12" y1="21" x2="12" y2="23" stroke="currentColor" strokeWidth="2"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" stroke="currentColor" strokeWidth="2"/>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" stroke="currentColor" strokeWidth="2"/>
              <line x1="1" y1="12" x2="3" y2="12" stroke="currentColor" strokeWidth="2"/>
              <line x1="21" y1="12" x2="23" y2="12" stroke="currentColor" strokeWidth="2"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" stroke="currentColor" strokeWidth="2"/>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <div>
              <h2>Appearance</h2>
              <p>Customize how the application looks</p>
            </div>
          </div>
          <button 
            className="collapse-btn"
            onClick={(e) => { e.stopPropagation(); toggleSection('appearance'); }}
            aria-label={collapsedSections.appearance ? 'Expand' : 'Collapse'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ transform: collapsedSections.appearance ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
              <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {!collapsedSections.appearance && (
          <div className="settings-group">
          {/* Theme selector removed - locked to snow theme */}

          <div className="setting-item">
            <div className="setting-info">
              <label>Font Size</label>
              <span className="setting-description">Adjust text size for better readability</span>
            </div>
            <select
              value={settings.fontSize}
              onChange={(e) => handleSelect('fontSize', e.target.value)}
              className="select-input"
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          </div>
        </div>
        )}
      </div>

      {/* Chat Preferences */}
      <div className="settings-section">
        <div className="section-header" onClick={() => toggleSection('chat')} style={{ cursor: 'pointer' }}>
          <div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <div>
              <h2>Chat Preferences</h2>
              <p>Manage chat behavior and display options</p>
            </div>
          </div>
          <button 
            className="collapse-btn"
            onClick={(e) => { e.stopPropagation(); toggleSection('chat'); }}
            aria-label={collapsedSections.chat ? 'Expand' : 'Collapse'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ transform: collapsedSections.chat ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
              <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {!collapsedSections.chat && (
          <div className="settings-group">
          <div className="setting-item">
            <div className="setting-info">
              <label>Auto-save Chats</label>
              <span className="setting-description">Automatically save chat history</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.autoSaveChats}
                onChange={() => handleToggle('autoSaveChats')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Typing Indicator</label>
              <span className="setting-description">Show when AI is generating a response</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.showTypingIndicator}
                onChange={() => handleToggle('showTypingIndicator')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Message Format</label>
              <span className="setting-description">Choose how messages are displayed</span>
            </div>
            <select
              value={settings.messageFormat}
              onChange={(e) => handleSelect('messageFormat', e.target.value)}
              className="select-input"
            >
              <option value="plain">Plain Text</option>
              <option value="formatted">Formatted</option>
              <option value="markdown">Markdown</option>
            </select>
          </div>
        </div>
        )}
      </div>

      {/* Privacy Settings */}
      <div className="settings-section">
        <div className="section-header" onClick={() => toggleSection('privacy')} style={{ cursor: 'pointer' }}>
          <div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <div>
              <h2>Privacy & Data</h2>
              <p>Control your data and privacy settings</p>
            </div>
          </div>
          <button 
            className="collapse-btn"
            onClick={(e) => { e.stopPropagation(); toggleSection('privacy'); }}
            aria-label={collapsedSections.privacy ? 'Expand' : 'Collapse'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ transform: collapsedSections.privacy ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
              <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {!collapsedSections.privacy && (
          <div className="settings-group">
          <div className="setting-item">
            <div className="setting-info">
              <label>Data Collection</label>
              <span className="setting-description">Allow collection of usage data for improvement</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.dataCollection}
                onChange={() => handleToggle('dataCollection')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Share Analytics</label>
              <span className="setting-description">Help us improve by sharing anonymous analytics</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.shareAnalytics}
                onChange={() => handleToggle('shareAnalytics')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        )}
      </div>

      {/* Notification Settings */}
      <div className="settings-section">
        <div className="section-header" onClick={() => toggleSection('notifications')} style={{ cursor: 'pointer' }}>
          <div>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <div>
              <h2>Notifications</h2>
              <p>Manage how you receive updates</p>
            </div>
          </div>
          <button 
            className="collapse-btn"
            onClick={(e) => { e.stopPropagation(); toggleSection('notifications'); }}
            aria-label={collapsedSections.notifications ? 'Expand' : 'Collapse'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ transform: collapsedSections.notifications ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>
              <polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {!collapsedSections.notifications && (
          <div className="settings-group">
          <div className="setting-item">
            <div className="setting-info">
              <label>Email Notifications</label>
              <span className="setting-description">Receive updates via email</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={() => handleToggle('emailNotifications')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Chat Notifications</label>
              <span className="setting-description">Get notified about chat updates</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.chatNotifications}
                onChange={() => handleToggle('chatNotifications')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Document Notifications</label>
              <span className="setting-description">Get notified when documents are processed</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.documentNotifications}
                onChange={() => handleToggle('documentNotifications')}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
        )}
      </div>

      {/* Actions */}
      <div className="settings-actions">
        <button className="reset-btn" onClick={handleResetSettings}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <polyline points="1 4 1 10 7 10" stroke="currentColor" strokeWidth="2"/>
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Reset to Default
        </button>
        <button className="save-btn" onClick={handleSaveSettings}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" stroke="currentColor" strokeWidth="2"/>
            <polyline points="17 21 17 13 7 13 7 21" stroke="currentColor" strokeWidth="2"/>
            <polyline points="7 3 7 8 15 8" stroke="currentColor" strokeWidth="2"/>
          </svg>
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default Settings;
